<script>
  import { onMount, onDestroy } from 'svelte';
  import CardStatus from '../components/Cards/CardStatus.svelte';
  import DashboardLoading from '../components/Cards/DashboardLoading.svelte';
  import {
    fetchEgresoCabsForPickerBoard,
    claimEgresoCabForPicker,
  } from '../controllers/egresoCabController.js';
  import { DASHBOARD_CONFIG, getStatusLabel } from '../config.js';
  import { getCurrentUserId } from '../lib/roles.js';
  import { subscribeSafe, removeSubscription } from '../lib/realtime.js';
  import pb from '../lib/pocketbase.js';

  const PICKER_STATUSES = DASHBOARD_CONFIG.pickerDisplayStatuses;
  const STA_AUTORIZADO = 'AUTORIZADO_POR_CREDITOS';
  const STA_ASIGNACION = 'ASIGNACION_MESA';

  let autorizadoItems = [];
  let asignacionItems = [];
  let isLoading = true;
  let isInitialLoad = true;
  let error = null;
  let claimingId = null;
  let reloadTimer;
  let searchQuery = '';
  let searchDebounce;

  // Must be reactive statements — template fn calls do NOT track array deps in Svelte
  $: itemsByStatus = {
    [STA_AUTORIZADO]: autorizadoItems,
    [STA_ASIGNACION]: asignacionItems,
  };

  function removeFromBoth(id) {
    autorizadoItems = autorizadoItems.filter((row) => row.id !== id);
    asignacionItems = asignacionItems.filter((row) => row.id !== id);
  }

  /** Move card in UI immediately from realtime / claim (do not wait for refetch). */
  function moveToAsignacion(item) {
    if (!item?.id) return;
    autorizadoItems = autorizadoItems.filter((row) => row.id !== item.id);
    const next = {
      ...item,
      sta: STA_ASIGNACION,
      picker_name: item.picker_name || 'Tú',
    };
    const rest = asignacionItems.filter((row) => row.id !== item.id);
    asignacionItems = [next, ...rest];
  }

  function moveToAutorizado(item) {
    if (!item?.id) return;
    asignacionItems = asignacionItems.filter((row) => row.id !== item.id);
    const next = { ...item, sta: STA_AUTORIZADO };
    const rest = autorizadoItems.filter((row) => row.id !== item.id);
    autorizadoItems = [next, ...rest];
  }

  const fetchData = async () => {
    const userId = getCurrentUserId() || pb.authStore.model?.id;
    if (!userId) {
      error = 'Sesión no válida. Vuelve a iniciar sesión.';
      isLoading = false;
      return;
    }

    if (isInitialLoad) isLoading = true;
    error = null;

    try {
      const result = await fetchEgresoCabsForPickerBoard(userId, { searchQuery });
      autorizadoItems = result?.autorizado || [];
      asignacionItems = result?.asignacion || [];
    } catch (err) {
      error = 'Error al cargar el tablero de mesa.';
      console.error(err);
    } finally {
      isLoading = false;
      isInitialLoad = false;
    }
  };

  function scheduleFetchData(delay = 250) {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      fetchData();
    }, delay);
  }

  function handleSearchInput(event) {
    searchQuery = event.target.value;
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
      fetchData();
    }, 400);
  }

  function clearSearch() {
    searchQuery = '';
    clearTimeout(searchDebounce);
    fetchData();
  }

  function onEgresoCabEvent(e) {
    const record = e?.record;
    if (!record?.id) {
      scheduleFetchData();
      return;
    }

    if (e.action === 'delete') {
      removeFromBoth(record.id);
      return;
    }

    if (record.sta === STA_ASIGNACION) {
      // Sale del pool Autorizado siempre
      autorizadoItems = autorizadoItems.filter((row) => row.id !== record.id);
      // Solo mantener/actualizar en mi columna si ya es mío o lo estoy tomando
      if (
        claimingId === record.id ||
        asignacionItems.some((row) => row.id === record.id)
      ) {
        const prev =
          asignacionItems.find((row) => row.id === record.id) || record;
        moveToAsignacion({ ...prev, ...record, sta: STA_ASIGNACION });
      }
    } else if (record.sta === STA_AUTORIZADO) {
      const prev =
        asignacionItems.find((row) => row.id === record.id) ||
        autorizadoItems.find((row) => row.id === record.id) ||
        record;
      moveToAutorizado({ ...prev, ...record, sta: STA_AUTORIZADO });
    } else {
      removeFromBoth(record.id);
    }

    scheduleFetchData();
  }

  function onEgresoPickerEvent(e) {
    const record = e?.record;
    const userId = getCurrentUserId() || pb.authStore.model?.id;
    if (!record) {
      scheduleFetchData();
      return;
    }

    // Assigned to me → pull into Asignación
    if (e.action !== 'delete' && record.picker === userId && record.egresoCab) {
      const prev =
        autorizadoItems.find((r) => r.id === record.egresoCab) ||
        asignacionItems.find((r) => r.id === record.egresoCab);
      if (prev) {
        moveToAsignacion({ ...prev, picker_name: prev.picker_name || 'Tú' });
      }
    }

    // Unassigned / deleted → back to pool after refetch
    scheduleFetchData();
  }

  async function handlePickerClaim(event) {
    const { itemId, item } = event.detail;
    const userId = getCurrentUserId() || pb.authStore.model?.id;
    if (!userId || !itemId || claimingId === itemId) return;

    claimingId = itemId;

    // Optimistic — must stick on screen immediately
    moveToAsignacion({ ...item, picker_name: 'Tú' });

    const result = await claimEgresoCabForPicker(itemId, userId);
    claimingId = null;

    if (!result?.success) {
      await fetchData();
      return;
    }

    // Keep optimistic card; reconcile quietly
    scheduleFetchData();
  }

  function handleStatusChanged(event) {
    const { id } = event.detail;
    removeFromBoth(id);
  }

  onMount(() => {
    fetchData();
    subscribeSafe('egresoCab', '*', onEgresoCabEvent);
    subscribeSafe('egresoPicker', '*', onEgresoPickerEvent);
  });

  onDestroy(() => {
    clearTimeout(reloadTimer);
    clearTimeout(searchDebounce);
    removeSubscription('egresoCab', '*');
    removeSubscription('egresoPicker', '*');
  });
</script>

<div class="flex flex-wrap">
  <div class="w-full mb-4 px-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="text-blueGray-700 text-xl font-bold">Mis pedidos de mesa</h1>
        <p class="text-blueGray-500 text-sm">
          Arrastra de Autorizado por créditos a Asignación de mesa para tomar el pedido
        </p>
      </div>
      <div class="flex items-center gap-2 w-full sm:w-auto">
        <input
          type="text"
          bind:value={searchQuery}
          on:input={handleSearchInput}
          placeholder="Buscar ..."
          class="flex-1 sm:flex-none px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          on:click={clearSearch}
          class="px-4 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 focus:outline-none"
        >
          Limpiar
        </button>
      </div>
    </div>
  </div>

  {#if isLoading}
    <div class="w-full px-4">
      <DashboardLoading />
    </div>
  {:else if error}
    <div class="w-full px-4">
      <div class="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded">{error}</div>
    </div>
  {:else}
    <div class="w-full px-4">
      <div class="flex flex-wrap xl:flex-nowrap gap-4 overflow-x-auto pb-4">
        {#each PICKER_STATUSES as status (status)}
          <CardStatus
            title={getStatusLabel(status)}
            {status}
            items={itemsByStatus[status] || []}
            pickerMode={true}
            on:pickerClaim={handlePickerClaim}
            on:statusChanged={handleStatusChanged}
          />
        {/each}
      </div>
    </div>
  {/if}
</div>
